import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Web API for PWA install prompt (not in all TS libs) */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    __pwaBeforeInstallPrompt?: BeforeInstallPromptEvent | null;
  }
}

/**
 * Captures beforeinstallprompt as early as possible. The trials index.html includes an inline
 * script that registers a listener before Angular boots. This service reads from that global
 * and also adds its own listener (in case the event fires after the inline script but before
 * this service is created).
 */
@Injectable({
  providedIn: 'root',
})
export class PwaInstallService {
  private platformId = inject(PLATFORM_ID);

  /** The deferred install prompt, if captured. */
  readonly deferredPrompt = signal<BeforeInstallPromptEvent | null>(null);

  /** True after appinstalled event fires (user installed this session). */
  readonly appInstalled = signal(false);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Use event captured by inline script in index.html (runs before Angular)
    const captured = window.__pwaBeforeInstallPrompt;
    if (captured) {
      this.deferredPrompt.set(captured);
    }

    // Also register our own listener in case event fires after inline script but before this runs
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.handleAppInstalled);
  }

  private handleBeforeInstallPrompt = (e: Event): void => {
    e.preventDefault();
    const ev = e as BeforeInstallPromptEvent;
    window.__pwaBeforeInstallPrompt = ev;
    this.deferredPrompt.set(ev);
  };

  private handleAppInstalled = (): void => {
    window.__pwaBeforeInstallPrompt = null;
    this.deferredPrompt.set(null);
    this.appInstalled.set(true);
  };

  /** Triggers the native install prompt. Returns true if prompt was shown. */
  async promptInstall(): Promise<boolean> {
    const ev = this.deferredPrompt();
    if (!ev) return false;
    await ev.prompt();
    const { outcome } = await ev.userChoice;
    if (outcome === 'accepted') {
      this.deferredPrompt.set(null);
      window.__pwaBeforeInstallPrompt = null;
    }
    return outcome === 'accepted';
  }
}
